import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import notificationService from '../../services/notificationService';
import CSRFToken from '../../security/CSRFToken';
import { sanitizeText, sanitizeHtml, sanitizeUrl, sanitizeObject, validateEmail } from '../../security/sanitize';

import LoadingSpinner from '../../components/LoadingSpinner';
import { useFeedback } from '../../contexts/FeedbackContext';
import Breadcrumb from '../../components/common/Breadcrumb';

// Icons
import {
  Image,
  X,
  Star
} from 'lucide-react';

const ReviewSubmission = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { showSuccess, showError, handleAsyncOperation } = useFeedback();
  const [formData, setFormData] = useState({
    reviewText: '',
    rating: 0,
    contentLinks: [''],
    pros: [''],
    cons: ['']
  });
  const [images, setImages] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [videos, setVideos] = useState([]);
  const [videoUrls, setVideoUrls] = useState([]);
  const [videoUploading, setVideoUploading] = useState(false);

  // Placeholder data for demonstration
  const mockCampaign = {
    id: 'camp1',
    title: 'Summer Fashion Collection Review',
    brand: {
      name: 'StyleHub',
      logo: 'https://ui-avatars.com/api/?name=StyleHub&size=50&background=3B5BA5&color=fff&bold=true&format=svg'
    },
    requirements: 'Instagram post with detailed review, 3-5 photos',
    deadline: '2025-06-15'
  };

  useEffect(() => {
    const fetchCampaignDetails = async () => {
      if (!currentUser || !campaignId) {
        setLoading(false);
        return;
      }

      try {

        
        // First, get the campaign document
        const campaignRef = doc(db, 'campaigns', campaignId);
        const campaignSnap = await getDoc(campaignRef);
        
        if (!campaignSnap.exists()) {
          console.error('Campaign not found');
          setLoading(false);
          return;
        }
        
        const campaignData = campaignSnap.data();

        
        // Then, get the brand information
        const brandRef = doc(db, 'users', campaignData.brandId);
        const brandSnap = await getDoc(brandRef);
        let brandData = {};
        
        if (brandSnap.exists()) {
          brandData = brandSnap.data();

        }
        
        // Check if the current user is a participant in this campaign
        // First try with the combined ID format
        let participantRef = doc(db, 'campaignParticipants', `${campaignId}_${currentUser.uid}`);
        let participantSnap = await getDoc(participantRef);
        
        // If not found, try querying by campaignId and influencerId fields
        if (!participantSnap.exists()) {

          const participantsRef = collection(db, 'campaignParticipants');
          const q = query(
            participantsRef,
            where('campaignId', '==', campaignId),
            where('influencerId', '==', currentUser.uid)
          );
          
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            participantSnap = querySnapshot.docs[0];

          }
        }
        
        if (!participantSnap.exists()) {
          console.error('User is not a participant in this campaign');
          showError('You are not an approved participant for this campaign.', 'You need to apply and be approved before submitting a review.', 6000);
          // Still load the campaign data so user can see what they're missing
        } else {
          // Check if the participant status is approved
          const participantData = participantSnap.data();

          
          if (participantData.status !== 'approved') {
            console.error('User application is not approved for this campaign');
            showError(`Your application status is "${participantData.status}".`, 'Only approved participants can submit reviews.', 6000);
          } else {

          }
        }
        
        // Format the campaign data for the UI
        const formattedCampaign = {
          id: campaignSnap.id,
          title: campaignData.name || 'Unnamed Campaign',
          brand: {
            id: campaignData.brandId,
            name: brandData.displayName || brandData.companyName || 'Unknown Brand',
            logo: brandData.logo || brandData.profileImageUrl || brandData.logoUrl || '/assets/default-logo.png'
          },
          requirements: campaignData.contentGuidelines || 'No specific requirements provided',
          deadline: campaignData.endDate || 'No deadline specified',
          compensation: campaignData.compensationAmount || 'Not specified',
          compensationType: campaignData.compensationType || 'Not specified',
          status: campaignData.status || 'active'
        };
        
        setCampaign(formattedCampaign);
      } catch (error) {
        console.error("Error fetching campaign details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaignDetails();
  }, [campaignId, currentUser]);

  // Removed draft approval check - new simplified workflow

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleArrayInputChange = (index, field, value) => {
    const updatedArray = [...formData[field]];
    updatedArray[index] = value;
    setFormData({
      ...formData,
      [field]: updatedArray
    });
  };

  const addArrayItem = (field) => {
    setFormData({
      ...formData,
      [field]: [...formData[field], '']
    });
  };

  const removeArrayItem = (field, index) => {
    const updatedArray = [...formData[field]];
    updatedArray.splice(index, 1);
    setFormData({
      ...formData,
      [field]: updatedArray
    });
  };

  const handleRatingChange = (rating) => {
    setFormData({
      ...formData,
      rating
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length > 0) {
      setImages([...images, ...files]);
      
      // Create preview URLs
      const newImageUrls = files.map(file => URL.createObjectURL(file));
      setImageUrls([...imageUrls, ...newImageUrls]);
    }
  };

  const removeImage = (index) => {
    const updatedImages = [...images];
    const updatedImageUrls = [...imageUrls];
    
    updatedImages.splice(index, 1);
    updatedImageUrls.splice(index, 1);
    
    setImages(updatedImages);
    setImageUrls(updatedImageUrls);
  };

  const handleVideoChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file count (max 3 videos)
    if (videos.length + files.length > 3) {
      showError('You can upload a maximum of 3 videos.', 'Please remove some videos before adding more.', 5000);
      return;
    }
    
    // Validate file size (max 100MB per video)
    for (const file of files) {
      if (file.size > 100 * 1024 * 1024) {
        showError(`Video "${file.name}" is too large.`, 'Maximum size is 100MB per video.', 6000);
        return;
      }
    }
    
    if (files.length > 0) {
      setVideos([...videos, ...files]);
      
      // Create preview URLs
      const newVideoUrls = files.map(file => URL.createObjectURL(file));
      setVideoUrls([...videoUrls, ...newVideoUrls]);
    }
  };

  const removeVideo = (index) => {
    const updatedVideos = [...videos];
    const updatedVideoUrls = [...videoUrls];
    
    updatedVideos.splice(index, 1);
    updatedVideoUrls.splice(index, 1);
    
    setVideos(updatedVideos);
    setVideoUrls(updatedVideoUrls);
  };

  const uploadImages = async () => {
    if (images.length === 0) return [];
    
    setImageUploading(true);
    const uploadedUrls = [];
    
    try {
      for (let i = 0; i < images.length; i++) {
        const file = images[i];
        // Use the submissions path which is allowed in storage rules
        const imageRef = ref(storage, `submissions/${campaignId}/${currentUser.uid}/${Date.now()}_${file.name}`);
        

        
        await uploadBytes(imageRef, file);
        const url = await getDownloadURL(imageRef);

        uploadedUrls.push(url);
      }
      
      return uploadedUrls;
    } catch (error) {
      console.error("Error uploading images:", error);
      throw error;
    } finally {
      setImageUploading(false);
    }
  };

  const uploadVideos = async () => {
    if (videos.length === 0) return [];
    
    setVideoUploading(true);
    const uploadedUrls = [];
    
    try {
      for (let i = 0; i < videos.length; i++) {
        const file = videos[i];
        // Use the submissions path which is allowed in storage rules
        const videoRef = ref(storage, `submissions/${campaignId}/${currentUser.uid}/${Date.now()}_${file.name}`);
        
        await uploadBytes(videoRef, file);
        const url = await getDownloadURL(videoRef);
        
        uploadedUrls.push(url);
      }
      
      return uploadedUrls;
    } catch (error) {
      console.error("Error uploading videos:", error);
      throw error;
    } finally {
      setVideoUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser || !campaign) return;
    
    // Get CSRF token from form
    const form = new FormData(e.target);
    const csrfToken = form.get('csrfToken');
    
    // Validate CSRF token
    if (!csrfToken || csrfToken !== sessionStorage.getItem('csrfToken')) {
      showError('Security validation failed.', 'Please refresh the page and try again.', 6000);
      return;
    }
    
    // Sanitize user inputs
    const sanitizedReviewText = sanitizeText(formData.reviewText);
    
    // Validate form
    if (sanitizedReviewText.trim().length < 50) {
      showError("Review text should be at least 50 characters long", 'Please provide more detailed feedback about your experience.', 5000);
      return;
    }
    
    if (formData.rating === 0) {
      showError("Please provide a rating", 'A rating is required to submit your review.', 5000);
      return;
    }
    
    // Filter out empty content links, pros, and cons and sanitize them
    const contentLinks = formData.contentLinks
      .filter(link => link.trim() !== '')
      .map(link => sanitizeUrl(link));
    
    const pros = formData.pros
      .filter(pro => pro.trim() !== '')
      .map(pro => sanitizeText(pro));
    
    const cons = formData.cons
      .filter(con => con.trim() !== '')
      .map(con => sanitizeText(con));
    
    if (contentLinks.length === 0) {
      showError("Please provide at least one valid content link", 'Add links to your published content for this campaign.', 5000);
      return;
    }
    
    setSubmitting(true);
    // Toast clearing handled automatically by new system
    
    try {
      // Upload images and videos concurrently
      const [imageUrls, videoUrls] = await Promise.all([
        uploadImages(),
        uploadVideos()
      ]);
      
      // Create review document with sanitized values
      const reviewRef = await addDoc(collection(db, 'reviews'), {
        campaignId,
        influencerId: currentUser.uid,
        brandId: campaign.brand.id,
        reviewText: sanitizedReviewText,
        rating: formData.rating,
        contentLinks,  // Already sanitized above
        pros,          // Already sanitized above
        cons,          // Already sanitized above
        imageUrls,
        videoUrls,
        status: 'pending',
        campaignTitle: sanitizeText(campaign.title),
        influencerName: sanitizeText(currentUser.displayName) || 'Influencer',
        influencerEmail: validateEmail(currentUser.email) || '',
        influencerProfileImage: sanitizeUrl(currentUser.photoURL) || '',
        submittedAt: serverTimestamp()
      });
      

      
      // Find and update campaign participant status
      // First try with the combined ID format
      let participantRef;
      let participantId = `${campaignId}_${currentUser.uid}`;
      
      // Try to get the participant document
      const directRef = doc(db, 'campaignParticipants', participantId);
      const directSnap = await getDoc(directRef);
      
      // If not found with direct ID, query by campaignId and influencerId
      if (!directSnap.exists()) {

        const participantsRef = collection(db, 'campaignParticipants');
        const q = query(
          participantsRef,
          where('campaignId', '==', campaignId),
          where('influencerId', '==', currentUser.uid)
        );
        
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          participantId = querySnapshot.docs[0].id;

        } else {
          throw new Error('Could not find participant record');
        }
      }
      
      // Now update the participant document
      participantRef = doc(db, 'campaignParticipants', participantId);
      await updateDoc(participantRef, {
        submissionStatus: 'submitted',
        submissionDate: serverTimestamp(),
        reviewId: reviewRef.id
      });
      

      
      // Send notification to brand about new review submission
      try {
        // Create a custom notification for review submission (not in notificationService yet)
        await notificationService.createNotification(
          campaign.brand.id,
          'review_submission',
          'New Review Submission',
          `${currentUser.displayName || 'Influencer'} has submitted a review for your campaign: ${campaign.title}`,
          {
            relatedId: reviewRef.id,
            relatedType: 'review',
            campaignId,
            influencerId: currentUser.uid,
            rating: formData.rating
          }
        );

      } catch (notifError) {
        console.error('Error sending notification to brand:', notifError);
        // Don't fail the submission if notification fails
      }
      

      
      showSuccess("Review submitted successfully!", "Your review has been submitted and is now pending brand approval.", 4000);
      
      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/influencer/campaigns');
      }, 2000);
    } catch (error) {
      console.error("Error submitting review:", error);
      showError("Failed to submit review.", 'Please check your connection and try again.', 6000);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return null;
  }

  if (!campaign) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <h3 className="text-lg font-medium text-gray-900 mb-1">Campaign not found</h3>
        <p className="text-gray-500 mb-4">
          The campaign you're looking for doesn't exist or has been removed.
        </p>
        <Breadcrumb
          items={[
            { label: 'Campaigns', to: '/influencer/campaigns' },
            { label: 'Review Submission' },
          ]}
        />
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Campaigns', to: '/influencer/campaigns' },
          { label: 'Campaign', to: `/influencer/campaigns/${campaignId}` },
          { label: 'Review Submission' },
        ]}
      />
      
      <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center">
            {campaign.brand?.logo && campaign.brand.logo !== '/assets/default-logo.png' ? (
              <img 
                src={campaign.brand.logo} 
                alt={campaign.brand?.name || 'Brand'} 
                className="h-10 w-10 rounded-full mr-3"
                onError={(e) => {

                  e.target.onerror = null;
                  e.target.src = '/assets/default-logo.png';
                }}
              />
            ) : (
              <img 
                src="/assets/default-logo.png" 
                alt={campaign.brand?.name || 'Brand'} 
                className="h-10 w-10 rounded-full mr-3"
              />
            )}
            <div>
              <h3 className="text-lg font-medium text-gray-900">Submit Review for {campaign.title}</h3>
              <p className="text-sm text-gray-500">Share your honest feedback about the product</p>
            </div>
          </div>
        </div>
        

        
        <form onSubmit={handleSubmit} className="p-6">
          <CSRFToken />
          {/* Campaign Requirements Reminder */}
          <div className="mb-6 p-4 bg-blue-50 rounded-md">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Campaign Requirements</h4>
            <div className="text-sm text-blue-700 whitespace-pre-wrap font-mono leading-relaxed">
              {campaign.requirements || 'No requirements specified.'}
            </div>
            <p className="text-sm text-blue-700 mt-3 pt-3 border-t border-blue-200">
              <span className="font-medium">Deadline:</span> {campaign.deadline}
            </p>
          </div>
          
          {/* Rating */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Overall Rating
            </label>
            <div className="flex items-center">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleRatingChange(star)}
                  className="focus:outline-none"
                >
                  <Star 
                    className={`h-8 w-8 ${
                      star <= formData.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                    }`} 
                  />
                </button>
              ))}
              <span className="ml-2 text-sm text-gray-500">
                {formData.rating > 0 ? `${formData.rating} out of 5 stars` : 'Select a rating'}
              </span>
            </div>
          </div>
          
          {/* Review Text */}
          <div className="mb-6">
            <label htmlFor="reviewText" className="block text-sm font-medium text-gray-700 mb-2">
              Detailed Review
            </label>
            <textarea
              id="reviewText"
              name="reviewText"
              rows="6"
              value={formData.reviewText}
              onChange={handleInputChange}
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              placeholder="Share your honest experience with the product. What did you like? What could be improved? Would you recommend it to others?"
              required
            ></textarea>
            <p className="mt-1 text-xs text-gray-500">
              Minimum 50 characters. Current length: {formData.reviewText.length} characters
            </p>
          </div>
          
          {/* Pros and Cons */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Pros */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pros
              </label>
              {formData.pros.map((pro, index) => (
                <div key={index} className="flex mb-2">
                  <input
                    type="text"
                    value={pro}
                    onChange={(e) => handleArrayInputChange(index, 'pros', e.target.value)}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder={`Pro ${index + 1}`}
                  />
                  {formData.pros.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('pros', index)}
                      className="ml-2 text-gray-400 hover:text-red-500"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  )}
                </div>
              ))}
              {formData.pros.length < 5 && (
                <button
                  type="button"
                  onClick={() => addArrayItem('pros')}
                  className="mt-1 text-sm text-primary hover:text-primary-dark"
                >
                  + Add another pro
                </button>
              )}
            </div>
            
            {/* Cons */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cons
              </label>
              {formData.cons.map((con, index) => (
                <div key={index} className="flex mb-2">
                  <input
                    type="text"
                    value={con}
                    onChange={(e) => handleArrayInputChange(index, 'cons', e.target.value)}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder={`Con ${index + 1}`}
                  />
                  {formData.cons.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('cons', index)}
                      className="ml-2 text-gray-400 hover:text-red-500"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  )}
                </div>
              ))}
              {formData.cons.length < 5 && (
                <button
                  type="button"
                  onClick={() => addArrayItem('cons')}
                  className="mt-1 text-sm text-primary hover:text-primary-dark"
                >
                  + Add another con
                </button>
              )}
            </div>
          </div>
          
          {/* Content Links */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content Links
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Add links to your social media posts, videos, or blog reviews
            </p>
            {formData.contentLinks.map((link, index) => (
              <div key={index} className="flex mb-2">
                <div className="flex-grow">
                  <input
                    type="url"
                    value={link}
                    onChange={(e) => handleArrayInputChange(index, 'contentLinks', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="https://www.instagram.com/p/..."
                  />
                </div>
                {formData.contentLinks.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeArrayItem('contentLinks', index)}
                    className="ml-2 text-gray-400 hover:text-red-500"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            ))}
            {formData.contentLinks.length < 5 && (
              <button
                type="button"
                onClick={() => addArrayItem('contentLinks')}
                className="mt-1 text-sm text-primary hover:text-primary-dark"
              >
                + Add another link
              </button>
            )}
          </div>
          
          {/* Image Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Images
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                <Image className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="image-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none"
                  >
                    <span>Upload images</span>
                    <input
                      id="image-upload"
                      name="image-upload"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageChange}
                      className="sr-only"
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">
                  PNG, JPG, GIF up to 10MB each
                </p>
              </div>
            </div>
            
            {/* Image Previews */}
            {imageUrls.length > 0 && (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                {imageUrls.map((url, index) => (
                  <div key={index} className="relative">
                    <img
                      src={url}
                      alt={`Preview ${index + 1}`}
                      className="h-24 w-full object-cover rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 bg-white rounded-full p-1 shadow-sm text-gray-500 hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Video Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Videos
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="video-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none"
                  >
                    <span>Upload videos</span>
                    <input
                      id="video-upload"
                      name="video-upload"
                      type="file"
                      accept="video/*"
                      multiple
                      onChange={handleVideoChange}
                      className="sr-only"
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">
                  MP4, MOV, AVI up to 100MB each (max 3 videos)
                </p>
              </div>
            </div>
            
            {/* Video Previews */}
            {videoUrls.length > 0 && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {videoUrls.map((url, index) => (
                  <div key={index} className="relative">
                    <video
                      src={url}
                      className="h-32 w-full object-cover rounded-md"
                      controls
                      preload="metadata"
                    />
                    <button
                      type="button"
                      onClick={() => removeVideo(index)}
                      className="absolute top-1 right-1 bg-white rounded-full p-1 shadow-sm text-gray-500 hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                      Video {index + 1}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={() => navigate(`/influencer/campaigns/${campaignId}`)}
              className="mr-3 inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || imageUploading || videoUploading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              {submitting || imageUploading || videoUploading ? (
                <>
                  <LoadingSpinner size="xs" color="white" />
                  {imageUploading ? 'Uploading Images...' : videoUploading ? 'Uploading Videos...' : 'Submitting...'}
                </>
              ) : (
                'Submit Review'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewSubmission;
